import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Collapse } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';
import GoNogoBanner from './GoNogoBanner';
import CriterionCard from './CriterionCard';
import ComponentAccuracyChart from './ComponentAccuracyChart';
import ScenarioPopChart from './ScenarioPopChart';

export default function ValidationTab({ service }) {
    const { validationRuns, activeValidation, validationResults, loading, models } = service;
    const [expandAccuracy, setExpandAccuracy] = useState(false);
    const [expandScenarios, setExpandScenarios] = useState(false);

    useEffect(() => {
        service.refreshValidationRuns();
        if (!models) service.refreshModels();
    }, []);

    const goNogo = validationResults?.go_nogo;
    const criteria = goNogo?.criteria || [];
    const componentAccuracy = validationResults?.component_accuracy || [];
    const scenarioResults = validationResults?.scenario_results || [];

    const activeVersion = models?.active_version;
    const isRunning = activeValidation &&
        activeValidation.status !== 'completed' &&
        activeValidation.status !== 'failed';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => service.startValidation({
                        model_version: activeVersion,
                        n_buildings: 50,
                    })}
                    disabled={loading || isRunning || !activeVersion}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: COLORS.accent,
                        borderColor: alpha(COLORS.accent, 0.5),
                        '&:hover': {
                            borderColor: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.1),
                        },
                    }}
                >
                    {isRunning ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                    Full Validation Suite
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => service.startColdStartTest({
                        model_version: activeVersion,
                        n_buildings: 10,
                    })}
                    disabled={loading || isRunning || !activeVersion}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: COLORS.text.secondary,
                        borderColor: alpha(COLORS.text.secondary, 0.3),
                        '&:hover': {
                            borderColor: COLORS.text.secondary,
                            bgcolor: alpha(COLORS.text.secondary, 0.1),
                        },
                    }}
                >
                    Cold-Start Quick Test
                </Button>
            </Box>

            {!activeVersion && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: COLORS.solar,
                }}>
                    No active model version. Train and activate a model first.
                </Typography>
            )}

            {/* Progress */}
            {isRunning && (
                <Box sx={{
                    p: 2,
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                    borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: COLORS.accent }} />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: COLORS.text.secondary,
                        }}>
                            {activeValidation.status}...
                            {activeValidation.progress ? ` (${activeValidation.progress}%)` : ''}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Go/No-Go Banner */}
            {validationResults && (
                <GoNogoBanner passed={goNogo?.overall_pass} loading={isRunning} />
            )}

            {/* Criteria Cards */}
            {criteria.length > 0 && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 1.5,
                }}>
                    {criteria.map((c, i) => (
                        <CriterionCard
                            key={i}
                            name={c.name}
                            threshold={c.threshold}
                            actual={c.actual}
                            passed={c.passed}
                            priority={c.priority}
                        />
                    ))}
                </Box>
            )}

            {/* Component Accuracy (expandable) */}
            {componentAccuracy.length > 0 && (
                <Box sx={{
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}>
                    <Box
                        onClick={() => setExpandAccuracy(!expandAccuracy)}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            '&:hover': { bgcolor: alpha(COLORS.accent, 0.05) },
                        }}
                    >
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: COLORS.text.secondary,
                        }}>
                            Component Accuracy
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.muted,
                        }}>
                            {expandAccuracy ? '\u25B2' : '\u25BC'}
                        </Typography>
                    </Box>
                    <Collapse in={expandAccuracy}>
                        <Box sx={{ p: 2, pt: 0 }}>
                            <ComponentAccuracyChart componentAccuracy={componentAccuracy} />
                        </Box>
                    </Collapse>
                </Box>
            )}

            {/* Scenario PoP (expandable) */}
            {scenarioResults.length > 0 && (
                <Box sx={{
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}>
                    <Box
                        onClick={() => setExpandScenarios(!expandScenarios)}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            '&:hover': { bgcolor: alpha(COLORS.accent, 0.05) },
                        }}
                    >
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: COLORS.text.secondary,
                        }}>
                            Scenario PoP Results
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.muted,
                        }}>
                            {expandScenarios ? '\u25B2' : '\u25BC'}
                        </Typography>
                    </Box>
                    <Collapse in={expandScenarios}>
                        <Box sx={{ p: 2, pt: 0 }}>
                            <ScenarioPopChart scenarioResults={scenarioResults} />
                        </Box>
                    </Collapse>
                </Box>
            )}

            {/* Run History */}
            {validationRuns && validationRuns.length > 0 && (
                <Box>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: COLORS.text.muted,
                        mb: 1,
                    }}>
                        Run History
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {validationRuns.map(run => (
                            <Box
                                key={run.run_id}
                                onClick={() => service.loadValidationResults(run.run_id)}
                                sx={{
                                    p: 1.5,
                                    bgcolor: COLORS.bg.card,
                                    border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': {
                                        borderColor: alpha(COLORS.accent, 0.3),
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    color: COLORS.text.primary,
                                }}>
                                    {run.run_id}
                                </Typography>
                                <Box sx={{
                                    px: 0.75,
                                    py: 0.25,
                                    bgcolor: alpha(
                                        run.status === 'completed' ? COLORS.efficiency
                                            : run.status === 'failed' ? COLORS.loss
                                            : COLORS.solar,
                                        0.15
                                    ),
                                    borderRadius: 0.5,
                                }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: run.status === 'completed' ? COLORS.efficiency
                                            : run.status === 'failed' ? COLORS.loss
                                            : COLORS.solar,
                                    }}>
                                        {run.status}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
